import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { environment } from '@formrails/environments';
import { AssessmentMatrixDto } from '../models/assessment-matrix.dto';

/**
 * Assessment matrices available to application templates.
 *
 * Part of the assessment-matrix feature: application templates only, and
 * self-contained so the whole thing can be lifted out if matrices move onto
 * the workflow instead.
 */
@Injectable({ providedIn: 'root' })
export class AssessmentMatrixApiService {
  private http = inject(HttpClient);
  private apiUrl = environment.applicationApi;

  /**
   * Every matrix, sorted by name here because the API returns them unordered.
   * Cached for the session: the list is small, changes rarely, and the drawer
   * may be opened repeatedly.
   */
  private matrices$?: Observable<AssessmentMatrixDto[]>;

  getMatrices(): Observable<AssessmentMatrixDto[]> {
    this.matrices$ ??= this.http
      .get<AssessmentMatrixDto[]>(`${this.apiUrl}/AssessmentMatrix`)
      .pipe(
        map(matrices =>
          [...(matrices ?? [])].sort((a, b) => a.name.localeCompare(b.name))
        ),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.matrices$.pipe(catchError(() => of([])));
  }

  /** Drops the cache, so the next open re-reads the list. */
  refresh(): void {
    this.matrices$ = undefined;
  }
}
